import { logger } from 'cadenza/utils/logging';
import { selectIntegrationFeaturesById } from 'cadenza/api-client/maps-api';
import { context } from 'cadenza/map/context/context';
import { LayerProperty } from 'cadenza/map/layer-property';
import { getAndUpdateLayerSuggestedExtent, refreshLayer, zoomToResult } from 'cadenza/map/layer-utils';
import { ConditionApi } from 'cadenza/api-client/conditions/condition-api';
import { setLayerConditions } from 'cadenza/api-client/layer-conditions-api';
import {
  ExternalConditionChangeEvent
} from 'cadenza/map/custom/map-message-dispatcher/external-condition-change-event';
import { updateFeaturesInLayer } from 'cadenza/api-client/feature-layer-api';
import { CustomAppMessageType } from 'cadenza/map/custom/custom-app-message-type';
import { classicPostMessage } from 'cadenza/integration/classic-post-message';

export class MapMessageDispatcher {

  _map;
  _handlersByType;
  _mapActionService;

  constructor (map, mapActionService) {
    this._map = map;
    this._mapActionService = mapActionService;
    this._handlersByType = {
      [CustomAppMessageType.INCOMING.SELECT]: this._select.bind(this),
      [CustomAppMessageType.INCOMING.FILTER]: this._filter.bind(this),
      [CustomAppMessageType.INCOMING.FILTER_BATCH]: this._filterBatch.bind(this),
      [CustomAppMessageType.INCOMING.EXPORT_MAP]: this._exportMap.bind(this),
      [CustomAppMessageType.INCOMING.LIVE_LAYER]: this._liveLayer.bind(this),
      [CustomAppMessageType.INCOMING.FIND_LOCATION]: this._findLocation.bind(this),
      [CustomAppMessageType.INCOMING.SHOW_LAYER]: this._showLayer.bind(this),
      [CustomAppMessageType.INCOMING.HIDE_LAYER]: this._hideLayer.bind(this)
    };
  }

  dispatch (event) {
    const type = event.data.type;
    const handlerForType = this._handlersByType[type];
    if (handlerForType) {
      handlerForType(event);
    } else {
      logger.error('There is no handler for message of type', type);
    }
  }

  async _select ({ data }) {
    const { layerName, ids, compoundIds } = data;
    const layer = context(this._map).layers.find(l => l.get(LayerProperty.NAME) === layerName);
    if (!layer) {
      throw new Error(`no layer with name '${layerName}' found`);
    }
    const layerId = layer.get(LayerProperty.ID);
    const featureIds = ids || compoundIds;

    try {
      await selectIntegrationFeaturesById({
        layerId,
        featureIds
      });
      layer.set(LayerProperty.SELECTED_FEATURE_IDS, featureIds);
      refreshLayer(layer);
    } catch (error) {
      logger.error(`Could not make selection ${JSON.stringify(featureIds)} in layer ${layerName}`);
    }
  }

  async _filter ({ data }) {
    const { layerName, conditionName, values } = data;
    const targetLayer = getTargetLayer(this._map, layerName);
    try {
      const currentConditionValuesSetHash = targetLayer.get(LayerProperty.CONDITION_VALUES_SET_HASH);
      const conditionValuesSetHash = await setConditionValues(targetLayer, currentConditionValuesSetHash, conditionName, values);
      refreshAfterFilter(this._map, targetLayer, conditionValuesSetHash);
    } catch (error) {
      logger.error(`Failed to set condition ${conditionName} in layer ${layerName}`, error);
    }
  }

  async _filterBatch ({ data: { layerName, conditions } }) {
    const targetLayer = getTargetLayer(this._map, layerName);
    if (!conditions.length) {
      throw new Error('The "conditions" should be an array of at least 1 item');
    }

    // Please note that the conditionValuesSetHash obtained from setting 1 condition, has to be used to set the next,
    // this is the reason why we cannot simply use a Promise.all pattern here.
    try {
      let conditionValuesSetHash = targetLayer.get(LayerProperty.CONDITION_VALUES_SET_HASH);
      for (let i = 0; i < conditions.length; i++) {
        const { conditionName, values } = conditions[i];
        conditionValuesSetHash = await setConditionValues(targetLayer, conditionValuesSetHash, conditionName, values);
      }
      refreshAfterFilter(this._map, targetLayer, conditionValuesSetHash);
    } catch (error) {
      logger.error(`Failed to set conditions ${JSON.stringify(conditions)} in layer ${layerName}`, error);
    }
  }

  _exportMap () {
    const mapCanvas = this._map.getTargetElement().querySelector('canvas');
    const mapData = mapCanvas.toDataURL();
    classicPostMessage.sendEvent({
      type: CustomAppMessageType.OUTGOING.EXPORT_MAP,
      mapData
    });
  }

  async _liveLayer ({ data: { featureCollection } } = {}) {
    const liveLayer = context(this._map).layers.find(layer => layer.get(LayerProperty.IS_LIVE_LAYER));
    const layerId = liveLayer.get(LayerProperty.ID);
    try {
      await updateFeaturesInLayer(layerId, featureCollection);
      // Update the SUGGESTED_EXTENT layer property, it will be used in the vector-layer-source by the MAX_LAYER_EXTENT strategy
      await getAndUpdateLayerSuggestedExtent(liveLayer, this._map);
      refreshLayer(liveLayer);
    } catch (error) {
      logger.error('Could not update features in layer', error);
    }
  }

  async _findLocation ({ data: { locationQuery, searchProviderIds } } = {}) {
    const gazetteer = await this._mapActionService.getGazetteer();
    gazetteer.search(locationQuery, searchProviderIds);
  }

  _showLayer ({ data: { layerName } } = {}) {
    toggleLayer(this._map, layerName, true);
  }

  _hideLayer ({ data: { layerName } } = {}) {
    toggleLayer(this._map, layerName, false);
  }

}

function getTargetLayer (map, layerName) {
  const targetLayer = context(map).layers
    .find(layer => layer.get(LayerProperty.VISIBLE_IN_LAYERLIST) && layer.get(LayerProperty.NAME) === layerName);
  if (!targetLayer) {
    throw new Error(`no layer with name '${layerName}' found`);
  }
  return targetLayer;
}

function setConditionValues (targetLayer, currentConditionValuesSetHash, conditionName, values) {
  const layerId = targetLayer.get(LayerProperty.ID);
  const conditionSetId = targetLayer.get(LayerProperty.CONDITION_DEFINITION_SET_ID);
  const api = new ConditionApi({
    conditionSetId,
    conditionName,
    conditionValuesSetHashGetter: () => currentConditionValuesSetHash
  });

  return api.setValueIntegration(values)
    .then(({ conditionValuesSetHash }) => setLayerConditions(layerId, conditionValuesSetHash).then(() => conditionValuesSetHash));
}

function refreshAfterFilter (map, targetLayer, conditionValuesSetHash) {
  targetLayer.set(LayerProperty.CONDITION_VALUES_SET_HASH, conditionValuesSetHash);
  refreshLayer(targetLayer);
  zoomToResult(map, targetLayer);
  context(map).pull(); // update the map
  targetLayer.dispatchEvent(new ExternalConditionChangeEvent(conditionValuesSetHash)); // update the condition panel
}

function toggleLayer (map, layerName, isVisible) {
  const layer = getTargetLayer(map, layerName);
  layer.setVisible(isVisible);
}
